import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { ArrowLeft, ArrowRight, ClipboardList, FileText, Landmark, Paperclip, Receipt } from "lucide-react"
import { Page, Button, Input, NumberControl, Select } from "./ui"
import PaymentRequestTypeCards from "./components/payment/PaymentRequestTypeCards"
import PaymentWizardStepper from "./components/payment/PaymentWizardStepper"
import NonPoPaymentBuilder from "./components/payment/create/NonPoPaymentBuilder"
import CreateSection from "./components/payment/create/CreateSection"
import PoPaymentTable from "./components/payment/create/PoPaymentTable"
import InvoiceFields from "./components/payment/create/InvoiceFields"
import PoSelectionModal from "./components/payment/create/PoSelectionModal"
import PoRowSync from "./components/payment/create/PoRowSync"
import AdditionalDocuments from "./components/payment/create/AdditionalDocuments"
import PaymentSummary from "./components/payment/create/PaymentSummary"
import PaymentRequestReview from "./components/payment/create/PaymentRequestReview"
import { PoBudgetAllocation, DirectBudgetAllocation, periodOf } from "./components/payment/PaymentBudgetAllocation"
import usePoLineAllocations from "./components/payment/create/usePoLineAllocations"
import "./components/payment/create/paymentRequestCreate.css"
import { useGetPurchasesQuery } from "shared/redux/endpoints/procurementPurchases"
import {
  useGetPaymentRequestQuery,
  useCreatePaymentRequestMutation,
  useUpdatePaymentRequestMutation,
  useSubmitPaymentRequestMutation,
  useResubmitPaymentRequestMutation,
  useUploadPaymentRequestDocumentMutation,
} from "shared/redux/endpoints/procurementPayments"
import { useSelector } from "react-redux"
import { getRoleCode } from "constant/Role"
import { useGetAuthUserCentersQuery } from "shared/redux/endpoints/auth"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { PAYMENT_REQUEST_STATUS } from "./status"
import { isAutoRejectedOutcome, AUTO_REJECT_MESSAGE } from "./actionResultFeedback"
import { PAYMENT_TYPE_PICKER_OPTIONS } from "./constants"
import { firstError } from "./fieldRules"
import { parseInvoiceXml } from "./invoiceXml"
import {
  WIZARD_STEPS,
  emptySharedPayment,
  emptyNonPoInvoice,
  purchaseToRow,
  includedRows,
  totalsOf,
  validatePoRow,
  validateInvoice,
  validateNonPo,
  validateShared,
  withAmount,
  percentFromAmount,
  withPercent,
  withInputMode,
  roundMoney,
  applyInvoiceXml,
  mergeHydration,
  buildPoLine,
  buildNonPoLine,
  collectUploads,
  emptyNonPoSpend,
  nonPoSpendTotal,
  nonPoSpendGap,
  buildNonPoSpendPayload,
  nonPoBudgetIssue,
  directOrderToState,
} from "./components/payment/create/paymentRequestModel"

// `centerScoped` (LXO): the PO picker only offers POs whose order request
// belongs to the center picked on the Payment list (`?center=`, falling back to
// the first linked center), and the flow never hops to the org-wide To Pay pages.
const CreatePaymentRequestContainer = ({ centerScoped = false }) => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const purchaseIdParam = searchParams.get("purchaseId")
  const isEdit = !!id

  const { data: editingRow, isLoading: editingLoading, isError: editingError } = useGetPaymentRequestQuery(id, { skip: !isEdit })
  // A Non-PO draft may list its hidden PO in linkedPurchases; `direct` wins.
  const editingIsDirect = isEdit && !!editingRow?.direct
  const editingHasPurchase = isEdit && !editingIsDirect ? (editingRow?.linkedPurchases?.length || 0) > 0 : false
  const typeIsFixed = isEdit || !!purchaseIdParam
  const isInfoRequested = isEdit && editingRow?.status === PAYMENT_REQUEST_STATUS.INFO_REQUESTED

  const [requestType, setRequestType] = useState(purchaseIdParam ? "po" : null)
  const [step, setStep] = useState(1)
  const [maxReachedStep, setMaxReachedStep] = useState(1)
  const [showErrors, setShowErrors] = useState(false)
  const [rows, setRows] = useState([])
  const [shared, setShared] = useState(emptySharedPayment)
  const [invoice, setInvoice] = useState(emptyNonPoInvoice)
  const [docs, setDocs] = useState([])
  const [directState, setDirectState] = useState(emptyNonPoSpend)
  const [budgetStatus, setBudgetStatus] = useState({})
  const [poSearch, setPoSearch] = useState("")
  const [modalOpen, setModalOpen] = useState(false)
  const [editSeeded, setEditSeeded] = useState(!isEdit)
  const allocationPeriod = useMemo(() => periodOf(), [])

  const isPoLinked = requestType === "po"
  const isDirect = requestType === "direct"
  const directLinesTotal = nonPoSpendTotal(directState.lines)
  const onBudgetStatus = useCallback((centerId, status) => {
    setBudgetStatus((prev) => {
      const old = prev[centerId]
      if (old && old.loading === status.loading && old.missing === status.missing && old.exceeds === status.exceeds) return prev
      return { ...prev, [centerId]: status }
    })
  }, [])

  const switchedCenterId = useSelector((state) => state.auth.centerId)
  // BE scopes payable POs by the active (switched) role, not every role in the token.
  const activeRoleCode = useSelector((state) => getRoleCode(state.auth?.role))
  const { data: userCenters, isFetching: userCentersLoading } = useGetAuthUserCentersQuery(undefined, {
    skip: !centerScoped,
  })
  const scopedCenterId = centerScoped
    ? ((userCenters || []).find((c) => String(c.centerId) === searchParams.get("center")) ||
        (userCenters || []).find((c) => String(c.centerId) === String(switchedCenterId)) ||
        userCenters?.[0])?.centerId
    : undefined
  const paymentListPath = `/procurement/payment-requests${scopedCenterId ? `?center=${scopedCenterId}` : ""}`
  const { data: payablePos, isFetching: payablePosFetching } = useGetPurchasesQuery(
    { payable: true, size: 200, center: scopedCenterId, role: activeRoleCode },
    { skip: !isPoLinked || isEdit || (centerScoped && !scopedCenterId) }
  )
  const payablePosLoading = payablePosFetching || userCentersLoading

  useEffect(() => {
    if (isEdit || !purchaseIdParam) return
    setRequestType("po")
    setRows((prev) =>
      prev.some((r) => r.purchaseId === String(purchaseIdParam)) ? prev : [purchaseToRow({ id: purchaseIdParam }), ...prev]
    )
  }, [isEdit, purchaseIdParam])

  useEffect(() => {
    if (!isEdit || !editingRow || editSeeded) return
    setRequestType(editingIsDirect ? "direct" : editingHasPurchase ? "po" : "no_po")
    setShared({
      paymentType: editingRow.paymentType || "normal",
      paymentTimeline: editingRow.paymentTimeline || "",
      bankReceiverName: editingRow.bankReceiverName || "",
      bankNameBranch: editingRow.bankNameBranch || "",
      bankAccount: editingRow.bankAccount || "",
      description: editingRow.description || "",
      bankTouched: true,
      batchLabel: editingRow.batchLabel || "",
    })
    if (editingHasPurchase) {
      const links = editingRow.linkedPurchases || []
      const multi = links.length > 1
      setRows(
        links.map((lp, idx) => {
          const amount = lp.allocatedAmount ?? (links.length === 1 ? editingRow.amount : "")
          return {
            ...purchaseToRow({
              id: lp.id,
              poCode: lp.poCode,
              vendorName: lp.vendorName || lp.supplierName,
              vendorId: lp.vendorId ?? lp.supplierId,
              total: lp.total ?? lp.totalAmount,
              remainingToPay: lp.remainingToPay,
              budgetCategoryId: lp.budgetCategoryId,
              region: lp.region,
              center: lp.center,
            }),
            amount: amount === "" || amount == null ? "" : Number(amount),
            amountTouched: true,
            inputMode: "amount",
            note: idx === 0 ? editingRow.note || "" : "",
            headroom: multi ? Number(lp.allocatedAmount) || 0 : Number(editingRow.amount) || 0,
            amountsLocked: multi,
          }
        })
      )
    } else if (editingIsDirect) {
      setDirectState(directOrderToState(editingRow.directOrder))
    }
    setInvoice({
      ...emptyNonPoInvoice(),
      invoiceSerial: editingRow.invoiceSerial || "",
      invoiceNumber: editingRow.invoiceNumber || "",
      invoiceDate: editingRow.invoiceDate ? String(editingRow.invoiceDate).slice(0, 10) : "",
      supplierTaxCode: editingRow.supplierTaxCode || "",
      amount: editingRow.amount ?? "",
      note: editingRow.note || "",
    })
    setEditSeeded(true)
  }, [isEdit, editingRow, editingHasPurchase, editingIsDirect, editSeeded])

  const onHydrate = useCallback((purchaseId, payload) => {
    setRows((prev) => {
      let changed = false
      const next = prev.map((row) => {
        if (row.purchaseId !== String(purchaseId)) return row
        const merged = mergeHydration(row, payload)
        if (merged !== row) changed = true
        return merged
      })
      return changed ? next : prev
    })
  }, [])

  useEffect(() => {
    const source = rows.find((r) => r.included && r.bankAccount)
    if (!source) return
    setShared((current) => {
      if (current.bankTouched || current.bankAccount) return current
      return {
        ...current,
        bankReceiverName: source.bankReceiverName,
        bankNameBranch: source.bankNameBranch,
        bankAccount: source.bankAccount,
      }
    })
  }, [rows])

  const updateRow = (purchaseId, recipe) => {
    setRows((prev) => prev.map((row) => (row.purchaseId === String(purchaseId) ? recipe(row) : row)))
  }

  const rowErrors = useMemo(() => {
    const map = {}
    rows.forEach((row) => {
      map[row.purchaseId] = validatePoRow(row)
    })
    return map
  }, [rows])

  const payingRows = includedRows(rows)
  const totals = totalsOf(rows)
  const amountByPurchase = useMemo(
    () => Object.fromEntries(includedRows(rows).map((r) => [r.purchaseId, r.amount])),
    [rows]
  )
  const poTotalByPurchase = useMemo(() => Object.fromEntries(rows.map((r) => [r.purchaseId, r.total])), [rows])
  const lineAllocations = usePoLineAllocations({
    payingRows,
    enabled: isPoLinked && editSeeded,
    editId: isEdit ? id : undefined,
    // Not withAmount: line amounts can carry decimals it would strip.
    onAmountChange: (purchaseId, amount) =>
      updateRow(purchaseId, (row) => ({
        ...row,
        amount,
        amountTouched: true,
        inputMode: "amount",
        percent: percentFromAmount(amount, row.remaining),
      })),
  })
  const allocationFileBase = isEdit ? editingRow?.paymentCode || "PR-draft" : "PR-draft"
  const anchor = payingRows[0] || rows[0] || null

  const issueFor = (target) => {
    if (!requestType) return "Choose how this payment relates to a Purchase Order first."
    if (isPoLinked) {
      if (!payingRows.length) return "Select at least one purchase order."
      for (const row of payingRows) {
        const msg = Object.values(rowErrors[row.purchaseId] || {})[0]
        if (msg) return `${row.poCode || "Purchase order"}: ${msg}`
      }
      const invoiceMsg = firstError(validateInvoice(invoice, { paymentType: shared.paymentType }))
      if (invoiceMsg) return invoiceMsg
    }
    if (isDirect) {
      const gap = nonPoSpendGap(directState)
      if (gap) return gap
    }
    if (target === 1) return null
    const sharedMsg = firstError(validateShared(shared))
    if (sharedMsg) return sharedMsg
    if (!isPoLinked) {
      const amount = isDirect ? directLinesTotal : invoice.amount
      const msg = Object.values(
        validateNonPo(invoice, {
          paymentType: shared.paymentType,
          description: shared.description,
          descriptionRequired: requestType === "no_po",
          amount,
        })
      )[0]
      if (msg) return msg
    }
    return null
  }

  const [createPr, { isLoading: creating }] = useCreatePaymentRequestMutation()
  const [updateDraft, { isLoading: updating }] = useUpdatePaymentRequestMutation()
  const [submitPr, { isLoading: submitting }] = useSubmitPaymentRequestMutation()
  const [resubmitPr, { isLoading: resubmitting }] = useResubmitPaymentRequestMutation()
  const [uploadDocument, { isLoading: uploading }] = useUploadPaymentRequestDocumentMutation()
  const busy = creating || updating || submitting || resubmitting || uploading

  const uploadFiles = async (prId, files) => {
    for (const item of files) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await uploadDocument({ id: prId, file: item.file, docType: item.docType }).unwrap()
      } catch (e) {
        showToastError(e?.data?.data?.message || e?.data?.message || `Couldn't upload ${item.file?.name || "a file"}.`)
      }
    }
  }

  const back = () => {
    const firstId = payingRows[0]?.purchaseId || purchaseIdParam
    navigate(
      isEdit
        ? `/procurement/payment-requests/${id}`
        : firstId && !centerScoped
        ? `/procurement/to-pay/${firstId}`
        : paymentListPath
    )
  }

  const directOrderArgs = () => (isDirect ? { directOrder: buildNonPoSpendPayload(directState) } : {})

  const editLine = () => {
    if (isPoLinked) {
      const primary = rows[0]
      const amount = primary?.amountsLocked ? editingRow?.amount : primary?.amount
      return buildPoLine({ ...primary, amount }, shared, invoice)
    }
    return buildNonPoLine({
      shared,
      invoice,
      amount: isDirect ? directLinesTotal : invoice.amount,
    })
  }

  const persist = async ({ submit }) => {
    const problem = issueFor(3)
    if (problem) {
      setShowErrors(true)
      showToastError(problem)
      return
    }
    const allocationProblem = lineAllocations.issue()
    if (allocationProblem) {
      showToastError(allocationProblem)
      return
    }
    const budgetProblem = submit && isDirect ? nonPoBudgetIssue(budgetStatus) : null
    if (budgetProblem) {
      showToastError(budgetProblem)
      return
    }
    try {
      if (isEdit) {
        await updateDraft({
          id,
          ...directOrderArgs(),
          ...editLine(),
          ...(isPoLinked ? { lines: lineAllocations.allLines() } : {}),
        }).unwrap()
        await uploadFiles(id, collectUploads(invoice, docs))
        if (submit && isInfoRequested) {
          const result = await resubmitPr({ id, note: "" }).unwrap()
          if (isAutoRejectedOutcome("resubmit", result, PAYMENT_REQUEST_STATUS.REJECTED)) showToastError(AUTO_REJECT_MESSAGE)
          else showToastSuccess("Payment request resubmitted for approval.")
        } else {
          showToastSuccess("Draft updated.")
        }
        navigate(`/procurement/payment-requests/${id}`)
        return
      }

      if (isPoLinked) {
        const primary = payingRows[0]
        const links = payingRows.map((row) => ({
          purchaseId: row.purchaseId,
          allocatedAmount: Number(row.amount) || 0,
          lines: lineAllocations.linesFor(row),
        }))
        const amount = links.reduce((sum, l) => sum + l.allocatedAmount, 0)
        const created = await createPr({ ...buildPoLine(primary, shared, invoice), amount, links }).unwrap()
        await uploadFiles(created.id, collectUploads(invoice, docs))
        if (submit) await submitPr(created.id).unwrap()
        showToastSuccess(submit ? "Payment request submitted for approval." : "Draft payment request saved.")
        navigate(centerScoped ? paymentListPath : `/procurement/to-pay/${primary.purchaseId}`)
        return
      }

      const created = await createPr({
        ...directOrderArgs(),
        ...buildNonPoLine({ shared, invoice, amount: directLinesTotal }),
      }).unwrap()
      await uploadFiles(created.id, collectUploads(invoice, docs))
      if (submit) await submitPr(created.id).unwrap()
      showToastSuccess(submit ? "Payment request submitted for approval." : "Draft payment request saved.")
      navigate(paymentListPath)
    } catch (e) {
      showToastError(e?.data?.data?.message || e?.data?.message || (submit ? "Couldn't submit. Please try again." : "Couldn't save. Please try again."))
    }
  }

  useEffect(() => {
    if (step !== 3) setBudgetStatus({})
  }, [step])

  const handleContinue = () => {
    const problem = issueFor(step)
    if (problem) {
      setShowErrors(true)
      showToastError(problem)
      return
    }
    const next = Math.min(step + 1, WIZARD_STEPS.length)
    setStep(next)
    setMaxReachedStep((m) => Math.max(m, next))
  }

  const readInvoiceXml = async (file) => {
    try {
      const data = parseInvoiceXml(await file.text())
      const xmlAmount = data.amount != null && data.amount !== "" ? roundMoney(data.amount) : null
      setInvoice((prev) => {
        const next = applyInvoiceXml(prev, data, file)
        return requestType === "no_po" && xmlAmount != null ? { ...next, amount: xmlAmount } : next
      })
      // With several POs there's no telling how the invoice splits, so only a single PO takes its amount.
      if (isPoLinked && payingRows.length === 1 && xmlAmount != null) {
        updateRow(payingRows[0].purchaseId, (row) => withAmount(row, String(xmlAmount)))
      }
      if (data.bankAccount || data.bankNameBranch) {
        setShared((current) => ({
          ...current,
          bankTouched: true,
          bankAccount: data.bankAccount || current.bankAccount,
          bankReceiverName: data.sellerName || current.bankReceiverName,
          bankNameBranch: data.bankNameBranch || current.bankNameBranch,
        }))
      }
      showToastSuccess(`Invoice ${data.invoiceSerial || ""}-${data.invoiceNumber || ""} parsed${data.sellerName ? ` (${data.sellerName})` : ""}.`)
    } catch (err) {
      const message = err?.message || "Couldn't read this invoice XML."
      setInvoice((prev) => ({ ...prev, xmlFile: file, xmlStatus: "error", xmlError: message, xmlData: null }))
      showToastError(message)
    }
  }

  const patchShared = (patch) => setShared((current) => ({ ...current, ...patch }))
  const patchInvoice = (patch) => setInvoice((current) => ({ ...current, ...patch }))
  const invoiceErrors = !showErrors
    ? {}
    : isPoLinked
    ? validateInvoice(invoice, { paymentType: shared.paymentType })
    : validateNonPo(invoice, {
        paymentType: shared.paymentType,
        description: shared.description,
        descriptionRequired: requestType === "no_po",
        amount: isDirect ? directLinesTotal : invoice.amount,
      })
  const sharedErrors = showErrors ? validateShared(shared) : {}

  const primaryLabel =
    step < 2 ? "Continue" : step === 2 ? "Continue to Review" : busy ? "Submitting…" : isInfoRequested ? "Resubmit" : isEdit ? "Save changes" : "Submit for Approval"

  const onPrimary = () => {
    if (step < 3) handleContinue()
    else persist({ submit: true })
  }

  const headerActions = (
    <>
      <Button variant="secondary" onClick={() => persist({ submit: false })} disabled={busy}>
        {busy ? "Saving…" : "Save as Draft"}
      </Button>
      <Button variant="primary" onClick={onPrimary} disabled={busy || (step === 1 && !requestType)} data-testid="prc-primary">
        {primaryLabel}
        {step < 3 && <ArrowRight aria-hidden />}
      </Button>
    </>
  )

  if (isEdit && (editingLoading || !editSeeded) && !editingError) {
    return (
      <Page breadcrumb={["Payment", "Payment requests", "Edit"]} title="Edit Payment Request">
        <p className="prc-help">Loading…</p>
      </Page>
    )
  }

  if (isEdit && (editingError || !editingRow)) {
    return (
      <Page breadcrumb={["Payment", "Payment requests", "Edit"]} title="Edit Payment Request">
        <section className="prc-card">
          <div className="prc-card-b">This payment request could not be loaded.</div>
        </section>
      </Page>
    )
  }

  if (
    isEdit &&
    editingRow &&
    editingRow.status !== PAYMENT_REQUEST_STATUS.DRAFT &&
    editingRow.status !== PAYMENT_REQUEST_STATUS.INFO_REQUESTED
  ) {
    return (
      <Page breadcrumb={["Payment", "Payment requests", editingRow.paymentCode || "Edit"]} title="This payment request is no longer editable">
        <Button variant="primary" onClick={() => navigate(`/procurement/payment-requests/${id}`)}>
          View details
        </Button>
      </Page>
    )
  }

  const title = isInfoRequested ? "Edit & Resubmit Payment Request" : isEdit ? "Edit Payment Request" : "New Payment Request"
  const subtitle = isEdit
    ? "Update this draft, review it, then save or resubmit."
    : "Create a payment request with or without purchase orders"

  return (
    <Page
      breadcrumb={["Payment", "Payment requests", isEdit ? editingRow?.paymentCode || "Edit" : "New Payment Request"]}
      title={title}
      subtitle={subtitle}
      actions={headerActions}
    >
      <div className="prc">
        {isPoLinked && rows.map((row) => <PoRowSync key={row.purchaseId} purchaseId={row.purchaseId} onHydrate={onHydrate} />)}
        {lineAllocations.syncs}

        <PaymentWizardStepper
          steps={WIZARD_STEPS}
          current={step}
          maxReached={maxReachedStep}
          onStepClick={(next) => {
            if (next > step) {
              for (let s = step; s < next; s += 1) {
                const problem = issueFor(s)
                if (problem) {
                  setShowErrors(true)
                  showToastError(problem)
                  if (s !== step) setStep(s)
                  return
                }
              }
              setMaxReachedStep((reached) => Math.max(reached, next))
            }
            setStep(next)
          }}
        />

        {step === 1 && (
          <>
            <CreateSection icon={<Receipt aria-hidden />} title="1. Select Payment Type" subtitle="Choose whether this payment is linked to purchase orders.">
              {requestType === "no_po" ? (
                <p className="prc-help" style={{ marginTop: 0 }}>
                  This draft is not linked to a purchase order. Update its invoice and payment details on the next step.
                </p>
              ) : (
                <PaymentRequestTypeCards
                  value={requestType}
                  onChange={(value) => {
                    setRequestType(value)
                    setShowErrors(false)
                  }}
                  disabled={busy || typeIsFixed}
                />
              )}
              {!requestType && <p className="prc-help">Choose a payment type to continue.</p>}
            </CreateSection>

            {isPoLinked && (
              <CreateSection
                icon={<ClipboardList aria-hidden />}
                title="2. Purchase Orders"
                subtitle="Add purchase orders and enter how much to pay on each PO."
                flush
              >
                <PoPaymentTable
                  rows={rows}
                  filter={poSearch}
                  onFilter={setPoSearch}
                  showErrors={showErrors}
                  errorsById={rowErrors}
                  disabled={busy}
                  paymentType={shared.paymentType}
                  onPaymentType={(paymentType) => patchShared({ paymentType })}
                  canAdd={!isEdit}
                  onAdd={() => setModalOpen(true)}
                  allIncluded={rows.length > 0 && rows.every((r) => r.included)}
                  onToggleAll={(checked) => setRows((prev) => prev.map((row) => ({ ...row, included: checked })))}
                  onToggleIncluded={(purchaseId, included) => updateRow(purchaseId, (row) => ({ ...row, included }))}
                  onRemove={(purchaseId) => setRows((prev) => prev.filter((row) => row.purchaseId !== String(purchaseId)))}
                  onAmount={(purchaseId, value) => updateRow(purchaseId, (row) => withAmount(row, value))}
                  onPercent={(purchaseId, value) => updateRow(purchaseId, (row) => withPercent(row, value))}
                  onMode={(purchaseId, mode) => updateRow(purchaseId, (row) => withInputMode(row, mode))}
                  onField={(purchaseId, patch) => updateRow(purchaseId, (row) => ({ ...row, ...patch }))}
                />
                {rows.some((r) => r.included && r.deliveryFee != null) && (
                  <p className="prc-help" style={{ padding: "0 18px 12px" }}>
                    Delivery fees on these POs are shown on the purchase for reference and are not part of the remaining balance.
                  </p>
                )}
              </CreateSection>
            )}

            {isPoLinked && (
              <CreateSection
                icon={<FileText aria-hidden />}
                title="Invoice"
                subtitle="One invoice for the whole payment request. Upload the XML e-invoice to fill these fields, or enter them yourself."
              >
                <InvoiceFields
                  invoice={invoice}
                  errors={invoiceErrors}
                  paymentType={shared.paymentType}
                  disabled={busy}
                  onChange={patchInvoice}
                  onXml={readInvoiceXml}
                />
              </CreateSection>
            )}

            {isDirect && (
              <CreateSection
                icon={<Receipt aria-hidden />}
                title="Non-PO payment details"
                subtitle="Not linked to any purchase order or supplier. A budget category and at least one line item are required — the order and purchase are created behind the scenes."
              >
                <NonPoPaymentBuilder value={directState} onChange={setDirectState} disabled={busy} />
              </CreateSection>
            )}

            {isPoLinked && payingRows.length > 0 && (
              <PoBudgetAllocation
                period={allocationPeriod}
                amountByPurchase={amountByPurchase}
                poTotalByPurchase={poTotalByPurchase}
                states={lineAllocations.states}
                editor={lineAllocations.editor}
                disabled={busy}
                fileBase={allocationFileBase}
              />
            )}

            {isDirect && <DirectBudgetAllocation period={allocationPeriod} value={directState} fileBase={allocationFileBase} />}

            {isPoLinked && (
              <>
                <CreateSection
                  icon={<Paperclip aria-hidden />}
                  title="Additional Documents"
                  subtitle="Attach supporting documents such as contract, delivery note, quotation, etc. These belong to the whole payment request, separate from the invoice."
                >
                  <AdditionalDocuments
                    files={docs}
                    disabled={busy}
                    onAdd={(files) => setDocs((prev) => [...prev, ...files])}
                    onRemove={(index) => setDocs((prev) => prev.filter((_, i) => i !== index))}
                  />
                </CreateSection>
                <CreateSection icon={<FileText aria-hidden />} title="General Note (optional)" subtitle="Add any additional information for this payment request.">
                  <textarea
                    className="prc-textarea"
                    value={shared.description}
                    placeholder="Add any additional information for this payment request..."
                    disabled={busy}
                    onChange={(e) => patchShared({ description: e.target.value })}
                  />
                </CreateSection>
                <CreateSection icon={<Landmark aria-hidden />} title="3. Payment Request Summary">
                  <PaymentSummary {...totals} />
                </CreateSection>
              </>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <CreateSection icon={<Landmark aria-hidden />} title="Enter Payment Information" subtitle="Planned date, payment type, and where the money should be sent.">
              {isPoLinked && (
                <div className="prc-banner">
                  Paying <strong>{payingRows.length}</strong> purchase order{payingRows.length === 1 ? "" : "s"}
                  {anchor?.supplierName ? <> from <strong>{anchor.supplierName}</strong></> : null}. Invoice details are on the first step.
                </div>
              )}
              <div className="prc-grid-2">
                <div>
                  <Input
                    type="date"
                    required
                    label="Planned payment date"
                    value={shared.paymentTimeline}
                    disabled={busy}
                    onChange={(e) => patchShared({ paymentTimeline: e.target.value })}
                  />
                  {sharedErrors.paymentTimeline && <span className="prc-field-error">{sharedErrors.paymentTimeline}</span>}
                </div>
                <Select
                  label="Payment type"
                  required
                  allowEmpty={false}
                  options={PAYMENT_TYPE_PICKER_OPTIONS}
                  value={shared.paymentType}
                  disabled={busy}
                  error={sharedErrors.paymentType}
                  onChange={(paymentType) => patchShared({ paymentType })}
                />
                <Input label={isDirect ? "Supplier / receiver name" : "Receiver name"} value={shared.bankReceiverName} error={sharedErrors.bankReceiverName} disabled={busy} onChange={(e) => patchShared({ bankReceiverName: e.target.value, bankTouched: true })} />
                <Input label="Bank name / branch" value={shared.bankNameBranch} error={sharedErrors.bankNameBranch} disabled={busy} onChange={(e) => patchShared({ bankNameBranch: e.target.value, bankTouched: true })} />
                <Input label="Bank account" value={shared.bankAccount} error={sharedErrors.bankAccount} disabled={busy} onChange={(e) => patchShared({ bankAccount: e.target.value, bankTouched: true })} />
              </div>
              {isPoLinked && <p className="prc-help">Bank details start from the purchase order and stay editable. An invoice XML that includes an account fills them in.</p>}
            </CreateSection>

            {!isPoLinked && (
              <CreateSection icon={<FileText aria-hidden />} title="Invoice" subtitle="Enter the invoice yourself, or upload an XML e-invoice to fill these fields. You can still edit them afterwards.">
                <InvoiceFields
                  invoice={invoice}
                  errors={invoiceErrors}
                  paymentType={shared.paymentType}
                  disabled={busy}
                  onChange={patchInvoice}
                  onXml={readInvoiceXml}
                >
                  <div>
                    <label className="prc-label">Payment amount</label>
                    <NumberControl
                      className={`prc-input${invoiceErrors.amount ? " is-invalid" : ""}`}
                      inputMode="numeric"
                      value={isDirect ? directLinesTotal : invoice.amount}
                      readOnly={isDirect}
                      disabled={busy}
                      onChange={(v) => patchInvoice({ amount: v.replace(/[^\d]/g, "") })}
                    />
                    {isDirect && <span className="prc-sub">Matches the line-item total.</span>}
                    {invoiceErrors.amount && <span className="prc-field-error">{invoiceErrors.amount}</span>}
                  </div>
                  <div>
                    <label className="prc-label">Note</label>
                    <input className="prc-input" value={invoice.note} disabled={busy} onChange={(e) => patchInvoice({ note: e.target.value })} />
                  </div>
                </InvoiceFields>
              </CreateSection>
            )}

            {!isPoLinked && (
              <>
                <CreateSection icon={<Paperclip aria-hidden />} title="Additional Documents" subtitle={isDirect ? "Attach supporting documents such as the contract, invoice, and budget or price approval." : "Supporting documents for this payment request, separate from the invoice PDF."}>
                  <AdditionalDocuments files={docs} disabled={busy} onAdd={(files) => setDocs((prev) => [...prev, ...files])} onRemove={(index) => setDocs((prev) => prev.filter((_, i) => i !== index))} />
                </CreateSection>
                <CreateSection icon={<FileText aria-hidden />} title={requestType === "no_po" ? "General Note" : "General Note (optional)"}>
                  <textarea
                    className={`prc-textarea${invoiceErrors.description ? " is-invalid" : ""}`}
                    value={shared.description}
                    placeholder="Add any additional information for this payment request..."
                    disabled={busy}
                    onChange={(e) => patchShared({ description: e.target.value })}
                  />
                  {invoiceErrors.description && <span className="prc-field-error">{invoiceErrors.description}</span>}
                </CreateSection>
              </>
            )}

            <CreateSection icon={<Landmark aria-hidden />} title="Payment Request Summary">
              {isPoLinked ? <PaymentSummary {...totals} /> : <PaymentSummary singleAmount={isDirect ? directLinesTotal : invoice.amount || 0} />}
            </CreateSection>
          </>
        )}

        {step === 3 && (
          <PaymentRequestReview
            requestType={requestType}
            rows={payingRows}
            shared={{
              ...shared,
              previewCategory: directState.budgetCategoryId,
              previewRegion: directState.region,
              previewCenter: directState.lines[0]?.centerId,
            }}
            invoice={invoice}
            directTitle={directState.title}
            directLines={directState.lines}
            directTotal={directLinesTotal}
            additionalDocs={docs}
            totals={totals}
            onEdit={() => setStep(1)}
            onBudgetStatus={onBudgetStatus}
          />
        )}

        <div className="prc-footer">
          <div className="prc-footer-l">
            <Button variant="secondary" onClick={back} disabled={busy}>
              Cancel
            </Button>
            {step > 1 && (
              <Button variant="ghost" leftIcon={<ArrowLeft aria-hidden />} onClick={() => setStep((current) => Math.max(1, current - 1))} disabled={busy}>
                Back
              </Button>
            )}
          </div>
          <div className="prc-footer-r">
            <Button variant="secondary" onClick={() => persist({ submit: false })} disabled={busy}>
              {busy ? "Saving…" : "Save as Draft"}
            </Button>
            <Button variant="primary" onClick={onPrimary} disabled={busy || (step === 1 && !requestType)}>
              {primaryLabel}
              {step < 3 && <ArrowRight aria-hidden />}
            </Button>
          </div>
        </div>
      </div>

      <PoSelectionModal
        open={modalOpen}
        purchases={payablePos?.items || []}
        loading={payablePosLoading}
        selectedIds={rows.map((r) => r.purchaseId)}
        anchor={anchor}
        onClose={() => setModalOpen(false)}
        onAdd={(list) => {
          setRows((prev) => {
            const ids = new Set(prev.map((r) => r.purchaseId))
            const next = [...prev]
            list.forEach((po) => {
              if (ids.has(String(po.id))) return
              next.push(purchaseToRow(po))
            })
            return next
          })
          setModalOpen(false)
        }}
      />
    </Page>
  )
}

export default CreatePaymentRequestContainer
